import {
  MapPin, ArrowRight, Calendar, Clock, User, Phone, Users, Package,
  ShieldCheck, StickyNote, ClipboardList,
} from 'lucide-react';

// Formats an ISO datetime string into a friendly "Mon, May 20 · 3:45 PM" label.
const formatDateTime = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const Row = ({ icon: Icon, label, value, testid, valueClass = 'text-zinc-900' }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 text-sm" data-testid={testid}>
      {Icon && <Icon className="w-4 h-4 text-zinc-400 mt-0.5 shrink-0" />}
      <div className="flex-1 min-w-0">
        <div className="text-xs text-zinc-500">{label}</div>
        <div className={`font-medium break-words ${valueClass}`}>{value}</div>
      </div>
    </div>
  );
};

const PhoneLink = ({ number }) =>
  number ? (
    <a href={`tel:${number}`} className="text-orange-600 hover:underline font-medium">
      {number}
    </a>
  ) : null;

/**
 * Rich, uniform job details panel used by Driver and Admin dashboards.
 * Renders every relevant field the backend returns for a job so the
 * driver/admin has a single source of truth once a job is created/assigned.
 */
export default function JobFullDetails({ job, variant = 'driver' }) {
  if (!job) return null;
  const isRide = job.job_type === 'ride';
  const isDelivery = job.job_type === 'delivery';
  const isPharmacy = isDelivery && job.delivery_category === 'pharmacy';

  const created = formatDateTime(job.created_at);
  const scheduled = formatDateTime(job.scheduled_at);

  return (
    <div
      className="mt-4 border border-zinc-200 rounded-xl bg-white overflow-hidden"
      data-testid={`job-full-details-${job.id}`}
    >
      {/* Header strip */}
      <div className="px-4 py-2.5 bg-gradient-to-r from-zinc-50 to-white border-b border-zinc-200 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 text-xs">
          <span className="font-semibold text-zinc-900 uppercase tracking-wide">
            {isRide ? 'Ride Details' : `${job.delivery_category || 'Package'} Delivery Details`}
          </span>
          <span className="text-zinc-400">·</span>
          <span className="font-mono text-zinc-500">#{job.id?.slice(0, 8)}</span>
        </div>
        <span
          className={`text-xs px-2 py-0.5 rounded-full font-medium ${
            job.status === 'completed'
              ? 'bg-green-100 text-green-700'
              : job.status === 'in_progress'
              ? 'bg-blue-100 text-blue-700'
              : job.status === 'accepted' || job.status === 'assigned'
              ? 'bg-orange-100 text-orange-700'
              : job.status === 'cancelled'
              ? 'bg-red-100 text-red-700'
              : 'bg-zinc-100 text-zinc-700'
          }`}
        >
          {job.status?.replace(/_/g, ' ')}
        </span>
      </div>

      <div className="p-4 space-y-4">
        {/* Timing */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Row icon={Calendar} label="Requested" value={created} testid="detail-created" />
          {scheduled && (
            <Row
              icon={Clock}
              label="Scheduled For"
              value={scheduled}
              testid="detail-scheduled"
              valueClass="text-orange-700"
            />
          )}
        </div>

        {/* People — customer + driver contact */}
        <div className="border-t border-zinc-100 pt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <div className="text-xs text-zinc-500 mb-1 flex items-center gap-1">
              <User className="w-3 h-3" /> Customer
            </div>
            <div className="font-medium text-zinc-900" data-testid="detail-customer-name">
              {job.user_name || '—'}
            </div>
            {job.user_phone && (
              <div className="text-sm flex items-center gap-1 mt-0.5">
                <Phone className="w-3 h-3 text-zinc-400" />
                <PhoneLink number={job.user_phone} />
              </div>
            )}
          </div>
          {(job.driver_name || variant === 'admin') && (
            <div>
              <div className="text-xs text-zinc-500 mb-1 flex items-center gap-1">
                <User className="w-3 h-3" /> Driver
              </div>
              <div className="font-medium text-zinc-900" data-testid="detail-driver-name">
                {job.driver_name || 'Not yet assigned'}
              </div>
              {job.driver_phone && (
                <div className="text-sm flex items-center gap-1 mt-0.5">
                  <Phone className="w-3 h-3 text-zinc-400" />
                  <PhoneLink number={job.driver_phone} />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Route */}
        <div className="border-t border-zinc-100 pt-3 space-y-2">
          <div className="flex items-start gap-2" data-testid="detail-pickup">
            <MapPin className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-zinc-500">Pickup</div>
              <div className="font-medium text-zinc-900 break-words">{job.pickup_address}</div>
            </div>
          </div>
          <div className="ml-2 border-l-2 border-dashed border-zinc-300 h-4" />
          <div className="flex items-start gap-2" data-testid="detail-dropoff">
            <MapPin className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-zinc-500">Dropoff</div>
              <div className="font-medium text-zinc-900 break-words">{job.dropoff_address}</div>
            </div>
          </div>
          {job.zone_name && (
            <div className="text-xs text-zinc-500 pt-1">
              Zone: <span className="font-medium text-purple-700">{job.zone_name}</span>
            </div>
          )}
        </div>

        {/* Ride passenger count */}
        {isRide && job.passenger_count && (
          <div className="border-t border-zinc-100 pt-3">
            <Row
              icon={Users}
              label="Passengers"
              value={String(job.passenger_count)}
              testid="detail-passengers"
            />
          </div>
        )}

        {/* Delivery order */}
        {isDelivery && (job.recipient_name || job.order_details || job.delivery_instructions) && (
          <div className="border-t border-zinc-100 pt-3 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Row
                icon={User}
                label="Recipient"
                value={job.recipient_name}
                testid="detail-recipient"
              />
              <Row
                icon={Phone}
                label="Recipient Phone"
                value={job.recipient_phone}
                testid="detail-recipient-phone"
              />
            </div>
            {job.order_details && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3" data-testid="detail-order">
                <div className="text-xs font-semibold uppercase tracking-wide text-orange-700 flex items-center gap-1 mb-1">
                  <Package className="w-3 h-3" /> Order / Items
                </div>
                <div className="text-sm text-zinc-900 whitespace-pre-wrap">{job.order_details}</div>
              </div>
            )}
            {job.delivery_instructions && (
              <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3" data-testid="detail-instructions">
                <div className="text-xs font-semibold uppercase tracking-wide text-zinc-600 flex items-center gap-1 mb-1">
                  <ClipboardList className="w-3 h-3" /> Delivery Instructions
                </div>
                <div className="text-sm text-zinc-900">{job.delivery_instructions}</div>
              </div>
            )}
          </div>
        )}

        {/* Pharmacy ID-check */}
        {isPharmacy && job.recipient_dob && (
          <div
            className="border-t border-zinc-100 pt-3"
            data-testid="detail-pharmacy-idcheck"
          >
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-1">
              <div className="text-xs font-semibold uppercase tracking-wide text-blue-800 flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" /> ID Check Required
              </div>
              <div className="text-sm text-blue-900">
                Verify recipient DOB: <span className="font-mono font-semibold">{job.recipient_dob}</span>
              </div>
              {job.prescription_number && (
                <div className="text-xs text-blue-900">
                  Rx #: <span className="font-mono">{job.prescription_number}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Customer notes */}
        {job.notes && (
          <div className="border-t border-zinc-100 pt-3">
            <div className="text-xs text-zinc-500 flex items-center gap-1 mb-1">
              <StickyNote className="w-3 h-3" /> Customer Notes
            </div>
            <div className="text-sm text-zinc-900 italic">&ldquo;{job.notes}&rdquo;</div>
          </div>
        )}

        {/* Pricing */}
        {(job.base_price != null || job.price != null) && (
          <div className="border-t border-zinc-100 pt-3 flex items-center justify-between text-sm">
            <div className="text-zinc-500">
              {job.base_price != null && (
                <>Base ${job.base_price.toFixed(2)}</>
              )}
              {job.zone_addon != null && job.zone_addon > 0 && (
                <> + Zone ${job.zone_addon.toFixed(2)}</>
              )}
            </div>
            <div className="text-lg font-bold text-zinc-900" data-testid="detail-total-price">
              ${job.price?.toFixed(2)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
